/* eslint-disable react/prop-types */
/**
 * CustomerRevenueOverview — Buchhalter-Übersicht: „Wer sind unsere Kunden
 * und was verdienen wir?" Ersetzt die BWA-Sicht für die Rolle backoffice.
 * Datenquelle: GET /api/admin/billing/unified?year&month (wie Abo-Rechnungen).
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, Users as UsersIcon, Euro,
  CheckCircle2, Send, Zap, Ban,
} from "lucide-react";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
  "August", "September", "Oktober", "November", "Dezember"];

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const INV_STATUS = {
  draft: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  sent: { label: "Versendet", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  paid: { label: "Bezahlt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Fehlgeschlagen", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  refunded: { label: "Erstattet", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  cancelled: { label: "Storniert", cls: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through" },
};

function Kpi({ icon: Icon, label, value, sub, tone = "zinc" }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Icon className={`h-4 w-4 text-${tone}-500`} />
        {label}
      </div>
      <div className="text-2xl font-bold text-zinc-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

export function CustomerRevenueOverview() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/billing/unified", { params: { year, month } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => toast.error(apiError(e, "Übersicht konnte nicht geladen werden")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year, month]);

  const k = data?.kpis || {};
  const rows = [...(data?.rows || [])].sort(
    (a, b) => (b.invoice?.total_gross ?? b.gross_estimate ?? 0) - (a.invoice?.total_gross ?? a.gross_estimate ?? 0)
  );

  return (
    <div className="space-y-5" data-testid="customer-revenue-overview">
      {/* Monats-Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Kunden & Umsatz</h2>
          <p className="text-xs text-zinc-500">Aktive Abonnenten und was wir im Monat verdienen</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="rev-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-semibold w-36 text-center" data-testid="rev-period">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="rev-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={UsersIcon} label="Aktive Kunden" value={k.subscribers ?? 0}
                 sub={`${k.stripe_ready ?? 0} mit Stripe-Mandat · ${k.blocked_count ?? 0} gesperrt`} tone="cyan" />
            <Kpi icon={Euro} label="Monatsumsatz (erwartet)" value={fmtEur(k.total_gross_estimate)}
                 sub="Grundgebühren + Akten, brutto" tone="emerald" />
            <Kpi icon={Send} label="In Rechnung gestellt" value={fmtEur(k.total_gross_invoiced)}
                 sub={`${(k.sent_count ?? 0) + (k.paid_count ?? 0)} Rechnungen versendet`} tone="sky" />
            <Kpi icon={CheckCircle2} label="Davon bezahlt" value={k.paid_count ?? 0}
                 sub={`${k.sent_count ?? 0} offen · ${k.draft_count ?? 0} Entwürfe`} tone="emerald" />
          </div>

          {/* Kundenliste */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-4 py-3">Kunde</th>
                    <th className="px-4 py-3">Tarif</th>
                    <th className="px-4 py-3 text-right">Akten</th>
                    <th className="px-4 py-3 text-right">Betrag</th>
                    <th className="px-4 py-3">Rechnung</th>
                    <th className="px-4 py-3">Einzug</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const inv = r.invoice;
                    const st = inv ? (INV_STATUS[inv.status] || INV_STATUS.draft) : null;
                    return (
                      <tr key={r.owner_id} className="border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`rev-row-${r.owner_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                            {r.owner_name}
                            {r.is_blocked && <Ban className="h-3.5 w-3.5 text-rose-500" title="Gesperrt" />}
                          </div>
                          <div className="text-[11px] text-zinc-400">{r.company || r.owner_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-zinc-700">{r.plan_name}</div>
                          <div className="text-[10px] text-zinc-400">{fmtEur(r.base_fee)} + {fmtEur(r.per_case_fee)}/Akte</div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">{r.effective_cases}</td>
                        <td className="px-4 py-3 text-right font-bold text-zinc-900">
                          {fmtEur(inv?.total_gross ?? r.gross_estimate)}
                        </td>
                        <td className="px-4 py-3">
                          {st ? (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${st.cls}`}>{st.label}</span>
                          ) : (
                            <span className="text-[11px] text-zinc-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {inv?.collection_status === "processing" && <span className="text-[11px] font-semibold text-sky-700">⏳ läuft</span>}
                          {(inv?.paid_via === "stripe" || inv?.collection_status === "succeeded") && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><Zap className="h-3 w-3" />Stripe</span>
                          )}
                          {inv?.collection_status === "failed" && (
                            <span className="text-[11px] font-semibold text-rose-700" title={inv.collection_error || ""}>❌ fehlgeschlagen</span>
                          )}
                          {!inv?.collection_status && !inv?.paid_via && (
                            r.stripe?.ready
                              ? <Badge className="bg-teal-50 text-teal-700 border border-teal-200 text-[10px]"><Zap className="h-3 w-3 mr-1" />bereit</Badge>
                              : <span className="text-[11px] text-zinc-400">kein Mandat</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-zinc-400">Keine aktiven Kunden in dieser Periode</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
